import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskAPI, projectAPI, dashboardAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import PunchInOutWidget from '../components/PunchInOutWidget';
import './ProjectLeadDashboard.css';

const Icon = ({ name, size = 20 }) => {
  const icons = {
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
    checkCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    alertCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
    activity: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  };
  return icons[name] || null;
};

const ProjectLeadDashboard = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTasks: 0,
    inProgress: 0,
    completed: 0,
    blocked: 0,
    teamMembers: 0
  });
  const [myProjects, setMyProjects] = useState([]);
  const [teamTasks, setTeamTasks] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [tasksRes, projectsRes] = await Promise.all([
        taskAPI.getTasks().catch(() => ({ data: { data: [] } })),
        projectAPI.getProjects().catch(() => ({ data: { data: [] } }))
      ]);

      const tasks = tasksRes.data.data || [];
      const projects = projectsRes.data.data || [];

      // Filter projects where user is lead
      const leadProjects = projects.filter(p => 
        p.projectLead?._id === user.id || p.projectLead === user.id
      );

      // Get tasks from lead projects
      const projectIds = leadProjects.map(p => p._id);
      const projectTasks = tasks.filter(t => projectIds.includes(t.project?._id));

      setStats({
        totalTasks: projectTasks.length,
        inProgress: projectTasks.filter(t => t.status === 'In Progress').length,
        completed: projectTasks.filter(t => t.status === 'Done').length,
        blocked: projectTasks.filter(t => t.status === 'Blocked').length,
        teamMembers: new Set(projectTasks.map(t => t.assignedTo?._id).filter(Boolean)).size
      });

      setMyProjects(leadProjects.slice(0, 5));
      setTeamTasks(projectTasks.slice(0, 8));
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'To Do': '#6B7280',
      'In Progress': '#3B82F6',
      'Done': '#10B981',
      'Blocked': '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Low': '#10B981',
      'Medium': '#F59E0B',
      'High': '#EF4444',
      'Critical': '#DC2626'
    };
    return colors[priority] || '#6B7280';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="lead-dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="lead-dashboard">
        <div className="lead-header">
          <div className="lead-header-content">
            <div className="lead-header-left">
              <h1 className="lead-title">Project Lead Dashboard</h1>
              <p className="lead-subtitle">Supervise your team and track progress</p>
            </div>
          </div>
        </div>

        <PunchInOutWidget />

        <div className="lead-metrics-grid">
          <div className="metric-card primary">
            <div className="metric-icon">
              <Icon name="checkCircle" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.totalTasks}</div>
              <div className="metric-label">Total Tasks</div>
              <div className="metric-detail">
                <span className="detail-item">Under supervision</span>
              </div>
            </div>
          </div>

          <div className="metric-card warning">
            <div className="metric-icon">
              <Icon name="activity" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.inProgress}</div>
              <div className="metric-label">In Progress</div>
              <div className="metric-detail">
                <span className="detail-item">{stats.blocked} Blocked</span>
              </div>
            </div>
          </div>

          <div className="metric-card success">
            <div className="metric-icon">
              <Icon name="target" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.completed}</div>
              <div className="metric-label">Completed</div>
              <div className="metric-detail">
                <span className="detail-item">
                  {stats.totalTasks > 0 ? Math.round((stats.completed / stats.totalTasks) * 100) : 0}% Done
                </span>
              </div>
            </div>
          </div>

          <div className="metric-card info">
            <div className="metric-icon">
              <Icon name="users" size={24} />
            </div>
            <div className="metric-content">
              <div className="metric-value">{stats.teamMembers}</div>
              <div className="metric-label">Team Members</div>
              <div className="metric-detail">
                <span className="detail-item">Active developers</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lead-content-grid">
          <div className="lead-section projects-section">
            <div className="section-header">
              <div className="section-title">
                <Icon name="briefcase" size={20} />
                <h2>My Projects</h2>
              </div>
              <button className="view-all-btn" onClick={() => navigate('/projects')}>View All</button>
            </div>
            <div className="projects-list">
              {myProjects.length === 0 ? (
                <div className="empty-state">
                  <Icon name="briefcase" size={32} />
                  <p>No projects assigned</p>
                </div>
              ) : (
                myProjects.map(project => (
                  <div key={project._id} className="project-item">
                    <div className="project-info">
                      <div className="project-name">{project.name}</div>
                      <div className="project-meta">
                        <span className="meta-item">
                          <Icon name="clock" size={14} />
                          {formatDate(project.endDate)}
                        </span>
                        <span className={`status-badge status-${project.status?.toLowerCase().replace(' ', '-')}`}>
                          {project.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="lead-section tasks-section">
            <div className="section-header">
              <div className="section-title">
                <Icon name="activity" size={20} />
                <h2>Team Tasks</h2>
              </div>
              <button className="view-all-btn" onClick={() => navigate('/tasks')}>View All</button>
            </div>
            <div className="tasks-list">
              {teamTasks.length === 0 ? (
                <div className="empty-state">
                  <Icon name="activity" size={32} />
                  <p>No tasks available</p>
                </div>
              ) : (
                teamTasks.map(task => (
                  <div key={task._id} className="task-item">
                    <div className="task-icon" style={{ backgroundColor: `${getStatusColor(task.status)}20`, color: getStatusColor(task.status) }}>
                      <Icon name="checkCircle" size={16} />
                    </div>
                    <div className="task-content">
                      <div className="task-title">{task.title}</div>
                      <div className="task-meta">
                        <span>{task.assignedTo?.name || 'Unassigned'}</span>
                        <span className="separator">•</span>
                        <span className="task-priority" style={{ color: getPriorityColor(task.priority) }}>
                          {task.priority}
                        </span>
                      </div>
                    </div>
                    <div className="task-status" style={{ color: getStatusColor(task.status) }}>
                      {task.status}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProjectLeadDashboard;
